import type { Filter, FilterOperator } from "./nodeQueryParams";
import { FilterOperatorsEnum } from "./nodeQueryParams";
import {
  normalizeSearchParams,
  type SearchParamsInput,
} from "./normalizeSearchParams";
import {
  buildUnitGroups,
  unitIdsOfGroup,
  type GroupableUnit,
  type UnitGroupConfig,
  type UnitGroupingMode,
} from "@autodromo/units";

interface GlobalFilterFieldConfig {
  fieldId: string;
}

export const GLOBAL_FILTER_FIELDS = {
  unit: { fieldId: "unit_id" },
} as const satisfies Record<string, GlobalFilterFieldConfig>;

export type GlobalFilterField = keyof typeof GLOBAL_FILTER_FIELDS;

export const GlobalFilterFieldsEnum = Object.fromEntries(
  Object.keys(GLOBAL_FILTER_FIELDS).map((key) => [key, key]),
) as Record<GlobalFilterField, GlobalFilterField>;

export function getGlobalFilterKey(field: GlobalFilterField): string {
  return `filter[${field}]`;
}

export const GLOBAL_UNIT_GROUP_KEY = "filter[unitGroup]";

export interface GlobalQueryParams {
  filters: Filter[];
}

export interface GlobalFilterContext {
  units?: GroupableUnit[];
  unitGroupingMode?: UnitGroupingMode | null;
  unitGroups?: UnitGroupConfig[] | null;
}

const GLOBAL_FILTER_REGEX = /^filter\[([^\[\]]+)\](?:\[([^\[\]]+)\])?$/;

const VALID_OPERATORS = new Set<string>(Object.values(FilterOperatorsEnum));

export function parseGlobalQueryParams(
  searchParams: SearchParamsInput,
  context?: GlobalFilterContext,
): GlobalQueryParams {
  const result: GlobalQueryParams = {
    filters: [],
  };

  const entries = normalizeSearchParams(searchParams);

  for (const [key, value] of entries) {
    if (key === GLOBAL_UNIT_GROUP_KEY) {
      result.filters.push(...expandUnitGroup(value, context));
      continue;
    }

    const match = key.match(GLOBAL_FILTER_REGEX);
    if (!match) continue;

    const [, urlField, rawOperator] = match;

    if (!(urlField in GLOBAL_FILTER_FIELDS)) continue;
    const fieldConfig = GLOBAL_FILTER_FIELDS[urlField as GlobalFilterField];

    const operator: FilterOperator =
      rawOperator && VALID_OPERATORS.has(rawOperator)
        ? (rawOperator as FilterOperator)
        : FilterOperatorsEnum.EQUALS;

    result.filters.push({
      fieldId: fieldConfig.fieldId,
      operator,
      value,
    });
  }

  return result;
}

function expandUnitGroup(
  groupSlugOrId: string,
  context?: GlobalFilterContext,
): Filter[] {
  if (!groupSlugOrId || !context?.units?.length) return [];

  const groups = buildUnitGroups({
    units: context.units,
    mode: context.unitGroupingMode,
    groups: context.unitGroups,
  });

  return unitIdsOfGroup(groups, groupSlugOrId).map((unitId) => ({
    fieldId: GLOBAL_FILTER_FIELDS.unit.fieldId,
    operator: FilterOperatorsEnum.EQUALS,
    value: unitId,
  }));
}
